import { Link } from "react-router-dom";
import {
  BrainCircuit,
  FlaskConical,
  Factory,
  TrendingDown,
  BarChart3,
  Activity,
  Layers,
  Sparkles,
  Database,
  ScrollText,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

// ────────────────────────────────────────────
// Landing page — marketing front door
// ────────────────────────────────────────────

const FEATURES = [
  {
    icon: BrainCircuit,
    title: "AI Data Analyst",
    description:
      "Ask questions in plain English. Get answers with charts, tables, and actionable insights. The AI writes SQL, queries your data, and explains what the numbers mean.",
    image: "/images/screenshots/ai-analyst.png",
  },
  {
    icon: FlaskConical,
    title: "SPC Control Charts",
    description:
      "Nelson Rules violation detection, control limits, and statistical process control across every measurement type. Catch drift before it becomes scrap.",
    image: "/images/screenshots/spc-charts.png",
  },
  {
    icon: Factory,
    title: "Production Analytics",
    description:
      "Track output, cycle times, and efficiency across machines and products. Compare periods, spot trends, and monitor daily volume by part.",
    image: "/images/screenshots/production.png",
  },
  {
    icon: TrendingDown,
    title: "Waste & Yield Analysis",
    description:
      "Identify material waste patterns, track scrap rates by part and material, and find where the savings are hiding in your process.",
    image: "/images/screenshots/waste.png",
  },
];

const CAPABILITIES = [
  { icon: Activity, label: "Real-time production monitoring" },
  { icon: FlaskConical, label: "Statistical process control (SPC)" },
  { icon: Layers, label: "Material variance tracking" },
  { icon: BrainCircuit, label: "AI-powered data analysis" },
  { icon: Sparkles, label: "Quality alert system (Nelson Rules)" },
  { icon: ScrollText, label: "Formulation management" },
  { icon: BarChart3, label: "Raw materials incoming QA" },
  { icon: Database, label: "Custom reporting via Data Explorer" },
];

export function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav />
      <Hero />
      <Features />
      <Capabilities />
      <CTABanner />
      <Footer />
    </div>
  );
}

// ────────────────────────────────────────────
// Nav
// ────────────────────────────────────────────

function Nav() {
  return (
    <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-bold">
            P
          </div>
          <span className="text-lg font-semibold tracking-tight">PlastiQ AI</span>
        </div>
        <Link
          to="/app"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Try the Demo
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </header>
  );
}

// ────────────────────────────────────────────
// Hero
// ────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative overflow-hidden py-20 sm:py-28 lg:py-36">
      {/* Glow effect */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/3 h-[500px] w-[800px] rounded-full bg-[oklch(0.488_0.243_264.376)] opacity-[0.07] blur-[120px]" />

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <div className="text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            Manufacturing Intelligence,{" "}
            <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">
              Powered by AI
            </span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground sm:text-xl">
            Real-time production analytics, SPC control charts, and an AI data analyst
            that speaks your language. See what a custom-built platform looks like for
            plastics manufacturing.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Link
              to="/app"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Explore the Demo
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="#features"
              className="inline-flex items-center gap-2 rounded-lg border border-border px-6 py-3 text-base font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              See Features
            </a>
          </div>
        </div>

        {/* Hero screenshot in browser frame */}
        <div className="relative mx-auto mt-16 max-w-5xl">
          <div className="rounded-xl border border-border/60 bg-card/50 p-1.5 shadow-2xl">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 rounded-t-lg bg-muted/50 px-4 py-2.5">
              <div className="flex gap-1.5">
                <div className="h-3 w-3 rounded-full bg-muted-foreground/20" />
                <div className="h-3 w-3 rounded-full bg-muted-foreground/20" />
                <div className="h-3 w-3 rounded-full bg-muted-foreground/20" />
              </div>
              <div className="ml-4 flex-1 rounded-md bg-background/50 px-3 py-1 text-xs text-muted-foreground">
                plastiq.flared.au
              </div>
            </div>
            <img
              src="/images/screenshots/overview.png"
              alt="PlastiQ AI Overview Dashboard showing production KPIs"
              className="w-full rounded-b-lg"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────
// Features
// ────────────────────────────────────────────

function Features() {
  return (
    <section id="features" className="border-t border-border/50 py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Everything you need to run a smarter factory
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            From raw material intake to finished product quality, PlastiQ AI gives you
            visibility across the entire injection moulding process.
          </p>
        </div>

        <div className="mt-16 grid gap-8 md:grid-cols-2">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="group overflow-hidden rounded-xl border border-border/60 bg-card transition-colors hover:border-border"
            >
              <div className="overflow-hidden">
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full transition-transform duration-300 group-hover:scale-[1.02]"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                    <feature.icon className="h-5 w-5 text-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold">{feature.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────
// Capabilities
// ────────────────────────────────────────────

function Capabilities() {
  return (
    <section className="border-t border-border/50 py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Built for plastics manufacturing
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            Not a generic dashboard with a manufacturing skin. Every feature is designed
            for the realities of injection moulding operations.
          </p>
        </div>

        <div className="mx-auto mt-12 grid max-w-3xl gap-4 sm:grid-cols-2">
          {CAPABILITIES.map((cap) => (
            <div
              key={cap.label}
              className="flex items-center gap-3 rounded-lg border border-border/40 bg-card/50 px-4 py-3"
            >
              <cap.icon className="h-5 w-5 shrink-0 text-muted-foreground" />
              <span className="text-sm font-medium">{cap.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────
// CTA Banner
// ────────────────────────────────────────────

function CTABanner() {
  return (
    <section className="border-t border-border/50 py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="relative overflow-hidden rounded-2xl border border-border/60 bg-card p-8 sm:p-12 lg:p-16">
          {/* Subtle glow */}
          <div className="pointer-events-none absolute right-0 top-0 h-64 w-64 rounded-full bg-[oklch(0.488_0.243_264.376)] opacity-[0.05] blur-[80px]" />

          <div className="relative text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Ready to see it in action?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              PlastiQ AI is a live demo with synthetic data. Explore every feature, chat
              with the AI analyst, and see what a custom analytics platform looks like.
            </p>
            <Link
              to="/app"
              className="mt-8 inline-flex items-center gap-2 rounded-lg bg-primary px-8 py-3.5 text-base font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Launch Demo
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ────────────────────────────────────────────
// Footer
// ────────────────────────────────────────────

function Footer() {
  return (
    <footer className="border-t border-border/50 py-12">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex flex-col items-center gap-8 sm:flex-row sm:justify-between">
          <div className="text-center sm:text-left">
            <div className="flex items-center gap-2.5">
              <div className="flex h-7 w-7 items-center justify-center rounded-md bg-primary text-primary-foreground text-xs font-bold">
                P
              </div>
              <span className="font-semibold">PlastiQ AI</span>
            </div>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              A demonstration platform by{" "}
              <a
                href="https://www.jezweb.com.au"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground underline underline-offset-2 hover:text-primary"
              >
                Jezweb
              </a>
              . We build custom analytics platforms for Australian manufacturers.
            </p>
          </div>

          <div className="flex flex-col items-center gap-4 sm:items-end">
            <a
              href="https://www.jezweb.com.au"
              target="_blank"
              rel="noopener noreferrer"
              className="transition-opacity hover:opacity-80"
            >
              <img
                src="https://www.jezweb.com.au/wp-content/uploads/2021/11/Jezweb-Logo-White-Transparent.svg"
                alt="Jezweb"
                className="h-8 dark:invert-0 invert"
              />
            </a>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>Custom software for manufacturing</span>
            </div>
          </div>
        </div>

        <div className="mt-8 border-t border-border/30 pt-6 text-center text-xs text-muted-foreground">
          2026 Jezweb Pty Ltd. PlastiQ AI contains synthetic demonstration data.
        </div>
      </div>
    </footer>
  );
}
